import { ChangeEvent, FC, KeyboardEvent, useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useSplunkActivityLogger, useSplunkLogger } from '@resolutionlife/ui';
import { isEmpty, isNil } from 'ramda';
import { BiEdit } from 'react-icons/bi';
import {
  FileDetailsFileFragment,
  useRenameFileMutation,
} from '../../../../graphql/generated';
import { validNameRegex } from '../../../../lib/storage-object/storage-object';

type FileRenameActionProps = {
  file: FileDetailsFileFragment;
};

export const FileRenameAction: FC<FileRenameActionProps> = ({ file }) => {
  const fileExtIndex = file.name.lastIndexOf('.');
  const filename = file.name.substring(0, fileExtIndex);
  const fileExtension = file.name.substring(fileExtIndex);

  const [newFilename, setNewFilename] = useState<string>(filename);
  const [isInvalidFilename, setIsInvalidFilename] = useState<boolean>(false);

  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const [renameFile, { loading }] = useRenameFileMutation();
  const activityLogger = useSplunkActivityLogger();
  const splunkLogger = useSplunkLogger();

  const handleFileRename = async () => {
    try {
      const { data } = await renameFile({
        variables: {
          cedant: file.cedant,
          input: {
            fileID: file.id,
            newFileName: newFilename,
          },
        },
        refetchQueries: ['GetFile'],
      });

      if (data) {
        toast({
          title: `"${file.name}" renamed to "${data.renameFile.name}"`,
          status: 'success',
          position: 'top',
        });

        activityLogger.logFileRename({
          cedantId: file.cedant,
          fileId: file.id,
          originalName: file.name,
          newName: data.renameFile.name,
        });

        closeModal();
      }
    } catch {
      toast({
        title: `Unable to rename "${file.name}"`,
        description: 'Please try again',
        status: 'error',
        position: 'top',
      });
      splunkLogger.error('File rename failed', { eventCategory: 'file_operation_error', cedantId: file.cedant, fileId: file.id, originalName: file.name, newName: newFilename });
    }
  };

  const preventSubmitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    event.key === 'Enter' && event.preventDefault();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filename = event.target.value;

    const isInvalid = !filename.match(validNameRegex);
    setIsInvalidFilename(isInvalid);

    setNewFilename(filename);
  };

  const resetAndClose = () => {
    setNewFilename(filename);
    closeModal();
  };

  const isSameFilename = filename === newFilename;

  const isInvalid =
    isNil(newFilename) ||
    isEmpty(newFilename) ||
    isSameFilename ||
    isInvalidFilename;

  return (
    <>
      <MenuItem
        onClick={openModal}
        icon={<Icon as={BiEdit} fontSize="16" />}
        data-testid="file-rename-button"
      >
        Rename
      </MenuItem>

      <Modal
        isOpen={isModalOpen}
        onClose={resetAndClose}
        isCentered
        // Prevent interrupting the operation on accidental click out
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent data-testid="file-rename-modal">
          <ModalHeader>Rename file</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={6}>
              <form onSubmit={handleFileRename} data-testid="rename-file-form">
                <FormControl isInvalid={isInvalid}>
                  <FormLabel pb={1}>Filename</FormLabel>
                  <InputGroup>
                    <Input
                      onKeyDown={preventSubmitOnEnter}
                      onChange={handleInputChange}
                      value={newFilename}
                      data-testid="rename-file-input"
                      zIndex={1} // error border gets overlapped by addon
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                    <InputRightAddon>
                      <Text fontWeight="medium">{fileExtension}</Text>
                    </InputRightAddon>
                  </InputGroup>
                  {isInvalidFilename && (
                    <FormErrorMessage data-testid="invalid-filename-error">{`Filenames cannot contain \\ ^ / ? * " > < : | %`}</FormErrorMessage>
                  )}
                </FormControl>
              </form>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={resetAndClose}
              data-testid="cancel-file-rename-button"
            >
              Cancel
            </Button>
            <Button
              ml={3}
              colorScheme="green"
              disabled={isInvalid}
              form="rename-file-form"
              isLoading={loading}
              onClick={handleFileRename}
              type="submit"
              data-testid="confirm-file-rename-button"
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
